/**
 * Subtle WebAudio cues for the voice capture flow (Phase C).
 *
 * No audio assets — tiny synthesized sine blips at very low gain. Every cue
 * is triggered by (or immediately follows) a user gesture, so there's no
 * autoplay risk. Everything is wrapped in try/catch: audio feedback is
 * non-critical and must never break the recorder.
 */

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  try {
    if (typeof window === "undefined") return null;
    const AC =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    if (!ctx) ctx = new AC();
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

function tone(freq: number, startOffset: number, duration: number, peak = 0.045) {
  const ac = audioCtx();
  if (!ac) return;
  try {
    const t0 = ac.currentTime + startOffset;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain).connect(ac.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  } catch {
    /* non-critical */
  }
}

export const voiceCues = {
  /** Soft rising blip when recording starts. */
  recordStart() {
    tone(620, 0, 0.09);
    tone(880, 0.09, 0.11, 0.05);
  },
  /** Soft falling blip when recording stops. */
  recordStop() {
    tone(880, 0, 0.09);
    tone(620, 0.09, 0.12, 0.04);
  },
  /** Gentle two-note "got it" when the card fills / a correction lands. */
  success() {
    tone(660, 0, 0.1);
    tone(990, 0.1, 0.16, 0.05);
  },
};
